import { type Dispatch, type SetStateAction, useRef } from "react";
import Settings from "@mui/icons-material/Settings";
import { Avatar, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import auth from "helpers/auth";
import {
  useGetCurrentUserQuery,
} from "api/api-users-v2";
import SkeletonLoad from "components/Common/SkeletonLoad";
import { useProfilePicture } from "hooks/useProfilePicture";
// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = ({
  handleRightDrawerToggle,
}: {
  handleRightDrawerToggle: Dispatch<SetStateAction<boolean>>;
}) => {
  const theme = useTheme();
  auth();

  // Fetch user data (always exists)
  const { data: user, isFetching: isUserLoading } = useGetCurrentUserQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  // Use our optimized profile picture hook
  const {
    data: profilePic,
    isError: isProfilePicError,
    isLoading: isProfilePicLoading,
  } = useProfilePicture(user);

  const anchorRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = () => {
    handleRightDrawerToggle((profile) => !profile);
  };

  const getAvatarContent = () => {
    if (isProfilePicLoading || isUserLoading) {
      return (
        <Avatar
          sx={{
            margin: "8px 0 8px 8px !important",
            cursor: "pointer",
            bgcolor: theme.palette.background.default,
            color: theme.palette.text.primary,
          }}
        />
      );
    }

    if (!isProfilePicError && profilePic?.url) {
      return (
        <Avatar
          src={profilePic.url}
          sx={{
            margin: "8px 0 8px 8px !important",
            cursor: "pointer",
            bgcolor: theme.palette.background.default,
            color: theme.palette.text.primary,
          }}
        />
      );
    }

    return (
      <Avatar
        sx={{
          margin: "8px 0 8px 8px !important",
          cursor: "pointer",
          bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
        }}
      >
        {user
          ? `${user?.first_name?.charAt(0)?.toUpperCase()}${user?.last_name?.charAt(0)?.toUpperCase()}`
          : ""}
      </Avatar>
    );
  };

  return (
    <>
      {isUserLoading && <SkeletonLoad bars={1} />}
      {!isUserLoading && user && (
        <Chip
          sx={{
            height: "48px",
            alignItems: "center",
            borderRadius: "27px",
            transition: "all .2s ease-in-out",
            borderColor: theme.palette.primary.light,
            backgroundColor: theme.palette.background.default,
            '&[aria-controls="menu-list-grow"], &:hover': {
              borderColor: theme.palette.dividerChannel,
              background: `${theme.palette.background.default}!important`,
              color: theme.palette.primary.light,
              "& svg": {
                stroke: theme.palette.primary.light,
              },
            },
            "& .MuiChip-label": {
              lineHeight: 0,
            },
          }}
          data-testid="profile-section"
          icon={getAvatarContent()}
          label={<Settings color="primary" />}
          variant="outlined"
          ref={anchorRef}
          aria-haspopup="true"
          onClick={handleToggle}
          color="primary"
        />
      )}
    </>
  );
};

export default ProfileSection;
